// src/components/site-editor/panels/LayoutPanel.js
import React from 'react';
import { useWebsiteBuilder } from '../../../contexts/WebsiteBuilderContext';
import LayoutSelector from '../../editors/LayoutSelector';

const LAYOUT_OPTIONS = [
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Clean lines, subtle borders, sophisticated restraint',
    preview: 'Straight edges, light borders, understated elegance'
  },
  {
    id: 'warm',
    name: 'Warm',
    description: 'Soft curves, gentle shadows, inviting feel',
    preview: 'Rounded corners, soft shadows, approachable design'
  },
  {
    id: 'bold',
    name: 'Bold',
    description: 'Strong presence, dramatic shadows, confident impact',
    preview: 'Sharp edges, deep shadows, commanding presence'
  }
];

const LayoutPanel = () => {
  const { state, actions } = useWebsiteBuilder();
  const { draft } = state;

  const handleLayoutChange = (layoutId) => {
    actions.updateDesign('layoutStyle', layoutId);
  };

  const handleSectionStyleChange = (styleId) => {
    actions.updateDesign('sectionStyle', styleId);
  };

  const currentColors = draft.design.customColors || {
    primary: '#7C3AED',
    secondary: '#FCD34D',
    background: '#FFFFFF',
    text: '#1F2937',
    accent: '#EC4899'
  };

  const sectionStyle = draft.design.sectionStyle || 'divided';

  return (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-1">Layout Style</h3>
        <p className="text-sm text-slate-600">Choose the overall design aesthetic for your site</p>
      </div>

      <LayoutSelector
        selectedLayout={draft.design.layoutStyle}
        onChange={handleLayoutChange}
        colors={currentColors}
        layoutOptions={LAYOUT_OPTIONS}
      />

      {/* Section Separation */}
      <div className="space-y-3 mt-8 pt-6 border-t border-gray-200">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-1">Section Separation</h3>
          <p className="text-sm text-slate-600">How sections are visually divided on your site</p>
        </div>

        <div className="space-y-3">
          {/* Clean */}
          <button
            onClick={() => handleSectionStyleChange('clean')}
            style={{
              width: '100%',
              padding: '16px',
              border: sectionStyle === 'clean' ? '2px solid #7c3aed' : '2px solid #E5E7EB',
              borderRadius: '8px',
              background: sectionStyle === 'clean' ? '#F3F0FF' : 'white',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s'
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-1">Clean</div>
                <div className="text-xs text-gray-600">Generous whitespace, no dividers</div>
              </div>
              {sectionStyle === 'clean' && (
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: '#7c3aed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'white' }} />
                </div>
              )}
            </div>

            {/* Visual Thumbnail */}
            <div style={{
              padding: '12px',
              background: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '6px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ height: '20px', background: '#F3F4F6', borderRadius: '4px' }} />
                <div style={{ height: '20px', background: '#F3F4F6', borderRadius: '4px' }} />
                <div style={{ height: '20px', background: '#F3F4F6', borderRadius: '4px' }} />
              </div>
            </div>
          </button>

          {/* Divided */}
          <button
            onClick={() => handleSectionStyleChange('divided')}
            style={{
              width: '100%',
              padding: '16px',
              border: sectionStyle === 'divided' ? '2px solid #7c3aed' : '2px solid #E5E7EB',
              borderRadius: '8px',
              background: sectionStyle === 'divided' ? '#F3F0FF' : 'white',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s'
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-1">Divided</div>
                <div className="text-xs text-gray-600">Subtle lines between sections</div>
              </div>
              {sectionStyle === 'divided' && (
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: '#7c3aed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'white' }} />
                </div>
              )}
            </div>

            {/* Visual Thumbnail */}
            <div style={{
              padding: '12px',
              background: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '6px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div style={{ height: '18px', background: '#F3F4F6', borderRadius: '4px' }} />
                <div style={{ height: '1px', background: currentColors.primary, opacity: 0.15, margin: '6px 8px' }} />
                <div style={{ height: '18px', background: '#F3F4F6', borderRadius: '4px' }} />
                <div style={{ height: '1px', background: currentColors.primary, opacity: 0.15, margin: '6px 8px' }} />
                <div style={{ height: '18px', background: '#F3F4F6', borderRadius: '4px' }} />
              </div>
            </div>
          </button>

          {/* Alternating */}
          <button
            onClick={() => handleSectionStyleChange('alternating')}
            style={{
              width: '100%',
              padding: '16px',
              border: sectionStyle === 'alternating' ? '2px solid #7c3aed' : '2px solid #E5E7EB',
              borderRadius: '8px',
              background: sectionStyle === 'alternating' ? '#F3F0FF' : 'white',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s'
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-1">Alternating</div>
                <div className="text-xs text-gray-600">Subtle shade variations</div>
              </div>
              {sectionStyle === 'alternating' && (
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: '#7c3aed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'white' }} />
                </div>
              )}
            </div>

            {/* Visual Thumbnail */}
            <div style={{
              padding: '12px',
              background: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '6px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div style={{ height: '18px', background: '#FFFFFF', borderRadius: '4px' }} />
                <div style={{ height: '18px', background: '#F3F4F6', borderRadius: '4px' }} />
                <div style={{ height: '18px', background: '#FFFFFF', borderRadius: '4px' }} />
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Layout Impact Note */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">What This Changes</h4>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>• <strong>Minimal:</strong> Straight edges, light borders, classic professional look</li>
          <li>• <strong>Warm:</strong> Rounded corners, soft shadows, welcoming and friendly</li>
          <li>• <strong>Bold:</strong> Strong shapes, deep shadows, modern and confident</li>
        </ul>
      </div>
    </div>
  );
};

export default LayoutPanel;
